import { onAuthStateChanged } from "firebase/auth";
import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../utils/axios";
import {
  auth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  User,
} from "../utils/firebase";
import { useStripe } from "./StripeContext";

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  premiumType: string;
  isPremium: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    name: string,
    surname: string
  ) => Promise<void>;
  logout: () => void;
  subscribeBasic: () => Promise<void>;
  subscribePro: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  loading: boolean;
  setPremiumType: (type: string) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [premiumType, setPremiumType] = useState<string>("none");
  const [loading, setLoading] = useState(true);
  const stripe = useStripe();

  // Propiedad calculada para mantener compatibilidad con el código existente
  const isPremium = premiumType !== "none";

  // Initialize auth state when the component mounts
  useEffect(() => {
    // Don't attempt to initialize auth until Stripe is ready or failed to load
    if (stripe.loading) {
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true);
      try {
        if (currentUser) {
          // Usuario autenticado
          setIsAuthenticated(true);
          setUser(currentUser);

          // Obtener información adicional del usuario (premium status) desde el backend
          try {
            // Obtener token ID de Firebase para autenticar con el backend
            const idToken = await currentUser.getIdToken();

            // Configurar el token en localStorage para compatibilidad con código existente
            localStorage.setItem("token", idToken);
            localStorage.setItem("user", currentUser.email || "");

            // Petición al backend para obtener información del usuario
            const response = await api.get("protected");
            const { premium_type, name } = response.data;

            // Update user object with name if available
            if (name && currentUser) {
              // @ts-ignore: We are extending the User type at runtime
              currentUser.name = name;
              setUser({ ...currentUser });
            }

            setPremiumType(premium_type || "none");
            localStorage.setItem("premiumType", premium_type || "none");
          } catch (error) {
            console.error("Error getting user info from backend:", error);
            setPremiumType("none");
            localStorage.setItem("premiumType", "none");
          }
        } else {
          // Usuario no autenticado
          setIsAuthenticated(false);
          setUser(null);
          setPremiumType("none");
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("premiumType");
        }
      } catch (error) {
        console.error("Error in auth state change:", error);
      } finally {
        setLoading(false);
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [stripe.loading]);

  // Debug effect to monitor auth state changes
  useEffect(() => {
    console.log("Auth state changed:", {
      isAuthenticated,
      user: user?.email,
      premiumType,
      isPremium,
    });
  }, [isAuthenticated, user, premiumType, isPremium]);

  const login = async (email: string, password: string) => {
    try {
      // Iniciar sesión con Firebase
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      // El listener de onAuthStateChanged ya se encargará de actualizar el estado
      return;
    } catch (error: any) {
      console.error("Login error:", error);
      throw new Error(error.message || "Invalid credentials");
    }
  };

  const register = async (
    email: string,
    password: string,
    name: string,
    surname: string
  ) => {
    try {
      // Registrar usuario en Firebase
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Registrar el usuario en el backend también
      try {
        const idToken = await userCredential.user.getIdToken();

        // Guardar el token y el email para mantener compatibilidad con código existente
        localStorage.setItem("token", idToken);
        localStorage.setItem("user", email);
        localStorage.setItem("premiumType", "none");

        // Crear el usuario en el backend usando un endpoint que no requiere autenticación previa
        await api.post("auth/firebase-register", {
          username: email, // Usando email como username
          uid: userCredential.user.uid,
          name: name,
          surname: surname,
        });

        // Set the name on the user object
        // @ts-ignore: We are extending the User type at runtime
        userCredential.user.name = name;

        // Actualizar el estado manualmente, sin llamar a protected
        setIsAuthenticated(true);
        setUser(userCredential.user);
        setPremiumType("none");
      } catch (backendError) {
        console.error("Error registering user in backend:", backendError);
        // Si falla el registro en el backend, eliminamos el usuario de Firebase
        await userCredential.user.delete();
        throw new Error("Error registering user");
      }

      return;
    } catch (error: any) {
      console.error("Registration error:", error);
      throw new Error(error.message || "Error registering user");
    }
  };

  // Este método solo llamará al método de Stripe y no intentará retornar la URL
  const subscribeBasic = async () => {
    try {
      if (!stripe) {
        throw new Error("Stripe not initialized");
      }

      // Llamar al método de Stripe pero no retornar nada
      // El componente PremiumDialog usará directamente stripe.subscribeBasic() que sí retorna la URL
      await stripe.subscribeBasic();
    } catch (error) {
      console.error("Error subscribing to Basic plan:", error);
      throw new Error("Could not subscribe to Basic plan");
    }
  };

  // Este método solo llamará al método de Stripe y no intentará retornar la URL
  const subscribePro = async () => {
    try {
      if (!stripe) {
        throw new Error("Stripe not initialized");
      }

      // Llamar al método de Stripe pero no retornar nada
      // El componente PremiumDialog usará directamente stripe.subscribePro() que sí retorna la URL
      await stripe.subscribePro();
    } catch (error) {
      console.error("Error subscribing to Pro plan:", error);
      throw new Error("Could not subscribe to Pro plan");
    }
  };

  const unsubscribe = async () => {
    try {
      if (!stripe) {
        throw new Error("Stripe not initialized");
      }

      // Use Stripe context to cancel subscription
      await stripe.cancelSubscription();

      // Update local state
      setPremiumType("none");
      localStorage.setItem("premiumType", "none");
    } catch (error) {
      console.error("Error unsubscribing:", error);
      throw new Error("Could not unsubscribe");
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      // El listener de onAuthStateChanged ya se encargará de actualizar el estado
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Show loading state if Stripe is still loading
  if (stripe.loading || loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="ml-3 text-lg">Cargando...</p>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        premiumType,
        isPremium,
        login,
        register,
        logout,
        subscribeBasic,
        subscribePro,
        unsubscribe,
        loading,
        setPremiumType,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
